/**
 * Pino Logger Configuration for React Frontend
 * Provides structured logging with different levels for dev/production
 *
 * SECURITY: VULN-012 FIX - Enhanced production logging with console suppression
 *
 * Production Mode:
 * - All debug/info logs are completely disabled
 * - Only error logs are kept for critical issues
 * - Console methods (log, debug, info) are suppressed to prevent information disclosure
 * - Console.error and console.warn are preserved for critical debugging
 *
 * Development Mode:
 * - Full debug logging enabled
 * - Pretty print for better readability
 * - All console methods available
 *
 * Security Benefits:
 * - Prevents information disclosure via browser console in production
 * - Reduces attack surface by hiding internal application details
 * - Improves performance by eliminating unnecessary logging overhead
 * - Defense in depth: Even if sensitive data is logged, it won't appear in console
 */

import pino from 'pino';

// Detect environment
const isProduction = import.meta.env.MODE === 'production';
const isDevelopment = import.meta.env.MODE === 'development';

// Configure Pino for browser environment
const logger = pino({
  // Only errors in production, full debug in development
  level: isDevelopment ? 'debug' : 'error',
  browser: {
    // Completely disabled in production for security
    disabled: isProduction,
    // Pretty print in development only
    serialize: isDevelopment,
    asObject: true,
  },
  // Base fields added to every log
  base: {
    env: import.meta.env.MODE,
    app: 'smartdoc-frontend',
  },
  // Timestamp format
  timestamp: pino.stdTimeFunctions.isoTime,
});

// SECURITY: Suppress console output in production
// This prevents information disclosure via browser DevTools console
if (isProduction) {
  // No-op function for suppressed console methods
  const noop = () => {};

  // Override console methods that could leak information
  // We keep console.error and console.warn for critical issues
  console.log = noop;
  console.debug = noop;
  console.info = noop;

  // Optional: Also suppress console.trace to prevent stack trace leakage
  // Uncomment if you want even stricter production logging
  // console.trace = noop;

  // Log suppression is active (this will only show in development)
  if (isDevelopment) {
    console.info('Production console suppression would be active in production build');
  }
}

// Create typed logger interface
export const log = {
  /**
   * Debug level - only in development
   * Use for detailed debugging info
   */
  debug: (msg: string, data?: unknown) => {
    logger.debug(data || {}, msg);
  },

  /**
   * Info level - general information
   * Use for normal application flow
   */
  info: (msg: string, data?: unknown) => {
    logger.info(data || {}, msg);
  },

  /**
   * Warn level - warning conditions
   * Use for recoverable errors or unexpected situations
   */
  warn: (msg: string, data?: unknown) => {
    logger.warn(data || {}, msg);
  },

  /**
   * Error level - error conditions
   * Use for errors that should be investigated
   */
  error: (msg: string, data?: unknown) => {
    logger.error(data || {}, msg);
  },

  /**
   * API call logging helper
   */
  api: {
    request: (method: string, url: string, data?: unknown) => {
      logger.debug({ method, url, data }, `API Request: ${method} ${url}`);
    },
    response: (method: string, url: string, status: number, data?: unknown) => {
      const level = status >= 400 ? 'error' : 'debug';
      logger[level]({ method, url, status, data }, `API Response: ${method} ${url} - ${status}`);
    },
    error: (method: string, url: string, error: Error) => {
      logger.error({ method, url, error: error.message, stack: error.stack }, `API Error: ${method} ${url}`);
    }
  },

  /**
   * User action logging helper
   */
  user: {
    action: (action: string, component: string, data?: unknown) => {
      const payload = data && typeof data === 'object' ? data : {};
      logger.info({ action, component, ...payload }, `User Action: ${action} in ${component}`);
    },
    navigation: (from: string, to: string) => {
      logger.debug({ from, to }, `Navigation: ${from} -> ${to}`);
    }
  },

  /**
   * Component lifecycle logging helper
   */
  component: {
    mount: (name: string, props?: Record<string, unknown>) => {
      logger.debug({ component: name, props }, `Component Mounted: ${name}`);
    },
    unmount: (name: string) => {
      logger.debug({ component: name }, `Component Unmounted: ${name}`);
    },
    error: (name: string, error: Error, errorInfo?: unknown) => {
      logger.error({ 
        component: name, 
        error: error.message, 
        stack: error.stack,
        errorInfo 
      }, `Component Error: ${name}`);
    }
  },

  /**
   * Performance logging helper
   */
  perf: {
    start: (operation: string) => {
      const start = performance.now();
      return {
        end: (data?: Record<string, unknown>) => {
          const duration = performance.now() - start;
          logger.debug({ operation, duration, ...data }, `Performance: ${operation} took ${duration.toFixed(2)}ms`);
        }
      };
    }
  }
};

export default log;
